import { useEffect, useState, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import {
  getNavPermissionContext,
  hasPermission,
  isOwnerLikeRole,
} from 'src/Common/permissions/cloudPosPermissions';

const ListWrapper = styled(Box)(
  ({ theme }) => `
        .MuiTouchRipple-root {
            display: none;
        }
        
        .MuiListItem-root {
            transition: ${theme.transitions.create(['color', 'fill'])};
            
            &.MuiListItem-indicators {
                padding: ${theme.spacing(1, 2)};
            
                .MuiListItemText-root {
                    .MuiTypography-root {
                        &:before {
                            height: 4px;
                            width: 22px;
                            opacity: 0;
                            visibility: hidden;
                            display: block;
                            position: absolute;
                            bottom: -10px;
                            transition: all .2s;
                            border-radius: ${theme.general.borderRadiusLg};
                            content: "";
                            background: ${theme.colors.primary.main};
                        }
                    }
                }

                &.active,
                &:active,
                &:hover {
                
                    background: transparent;
                
                    .MuiListItemText-root {
                        .MuiTypography-root {
                            &:before {
                                opacity: 1;
                                visibility: visible;
                                bottom: 0px;
                            }
                        }
                    }
                }
            }
        }
`
);

function HeaderMenu() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const storedRoles = localStorage.getItem('userRoles');
    if (storedRoles) {
      setRoles(storedRoles.split(',').map((role) => role.trim()));
    }
  }, []);

  const forms = [
    { name: "1. Balance Sheet", path: "/reports/balancesheet/", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "2. Profit Loss / Statement", path: "/reports/profitlossstatement/", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "3. Payable's", path: "/management/setup/system/getAllSuppliers", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "4. Receivable's", path: "/management/setup/system/getAllCustomers", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "5. Stock Ledgure", path: "/reports/stockLedgure", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "6. Payment Accounts Balances", path: "/reports/payment-accounts-balances", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "7. View All Reports", path: "/reports", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
  ];

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  // Show the reports dropdown when the user's role whitelist matches, when they were granted the
  // Reports hub permission (custom company roles), or when they are a company owner/admin.
  const { lookup, bypass } = getNavPermissionContext();
  const canSeeReports =
    isOwnerLikeRole(roles) || bypass || hasPermission(lookup, 'Reports', 'ReportsHub');
  const accessibleForms = forms.filter(
    form => canSeeReports || form.roles.some(role => roles.includes(role))
  );

  return (
    <>
      <ListWrapper
        sx={{
          display: {
            xs: 'none',
            md: 'block'
          }
        }}
      >
        <List disablePadding component={Box} display="flex">
          {accessibleForms.length > 0 && (
            <ListItem
              classes={{ root: 'MuiListItem-indicators' }}
              button
              ref={ref}
              onClick={handleOpen}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary={
                  <Box display="flex" alignItems="center">
                    Reports
                    <Box display="flex" alignItems="center" pl={0.3}>
                      <ExpandMoreTwoToneIcon fontSize="small" />
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          )}
        </List>
      </ListWrapper>

      <Menu anchorEl={ref.current} onClose={handleClose} open={isOpen}>
        {accessibleForms.map((form, index) => (
          <MenuItem
            key={index}
            sx={{ px: 3 }}
            component={NavLink}
            to={form.path}
          >
            {form.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default HeaderMenu;
